/**
 * Trading-vertical getters — the API the rest of the app uses.
 *
 * Nothing here falls back silently: a lookup for an unknown URL segment returns
 * `undefined` (the caller decides how to redirect), and asking for a default
 * vertical when none is enabled throws rather than pretending perpetuals exist.
 */
import { InstrumentTypeResponse } from "../../codegen-api";
import { FeatureFlagConfigError } from "./errors";
import {
  TRADING_TYPES,
  TRADING_TYPE_KEY_BY_INSTRUMENT,
  TRADING_TYPE_LIST,
} from "./registry";
import { isFeatureEnabled } from "./resolve";
import { TradingTypeDefinition, TradingTypeKey } from "./types";

export const getTradingTypeDefinition = (
  key: TradingTypeKey
): TradingTypeDefinition => TRADING_TYPES[key];

export const isTradingTypeEnabled = (key: TradingTypeKey): boolean =>
  isFeatureEnabled(key);

/**
 * The vertical that owns an instrument type, or `undefined`.
 *
 * The registry map is total over `InstrumentTypeResponse` at COMPILE time, but
 * these lookups also run on values that TypeScript never checked: a persisted
 * `lastViewedMarket`, a watchlist entry, an `instrument_type` off an API
 * response. Those can be any string, so the runtime signature is partial on
 * purpose — callers must handle `undefined` rather than crash the render.
 */
export const findTradingTypeByInstrument = (
  instrumentType?: InstrumentTypeResponse
): TradingTypeDefinition | undefined => {
  if (!instrumentType) {
    return undefined;
  }
  const key = TRADING_TYPE_KEY_BY_INSTRUMENT[instrumentType];
  return key ? TRADING_TYPES[key] : undefined;
};

/**
 * Same lookup for a value already known to come from the registry (a compile-
 * time-checked `InstrumentTypeResponse` the app itself produced).
 * @throws {FeatureFlagConfigError} if the instrument type has no vertical —
 * that would mean the registry itself is inconsistent.
 */
export const getTradingTypeByInstrument = (
  instrumentType: InstrumentTypeResponse
): TradingTypeDefinition => {
  const definition = findTradingTypeByInstrument(instrumentType);
  if (!definition) {
    throw new FeatureFlagConfigError(
      `No trading type owns instrument type ${JSON.stringify(instrumentType)}.`
    );
  }
  return definition;
};

/**
 * Is the vertical that trades this instrument type enabled?
 * An unknown/absent instrument type is simply not enabled — never an error,
 * because this runs on untrusted persisted and API data.
 */
export const isInstrumentTypeEnabled = (
  instrumentType?: InstrumentTypeResponse
): boolean => {
  const definition = findTradingTypeByInstrument(instrumentType);
  return definition ? isTradingTypeEnabled(definition.key) : false;
};

/** Enabled verticals, in display order. */
export const getEnabledTradingTypes = (): TradingTypeDefinition[] =>
  TRADING_TYPE_LIST.filter((definition) => isTradingTypeEnabled(definition.key));

/**
 * Enabled verticals a user can actually trade right now: implemented, and
 * backed by a real backend instrument type.
 */
export const getTradableTradingTypes = (): TradingTypeDefinition[] =>
  getEnabledTradingTypes().filter(
    (definition) => definition.implemented && definition.instrumentType !== null
  );

/** Enabled instrument types, in display order. */
export const getEnabledInstrumentTypes = (): InstrumentTypeResponse[] =>
  getTradableTradingTypes().map(
    (definition) => definition.instrumentType as InstrumentTypeResponse
  );

/**
 * Vertical for a URL segment (case-insensitive), regardless of its flag.
 * `undefined` for a segment no vertical claims.
 */
export const getTradingTypeBySegment = (
  segment?: string
): TradingTypeDefinition | undefined => {
  if (!segment) {
    return undefined;
  }
  const normalized = segment.toLowerCase();
  return TRADING_TYPE_LIST.find(
    (definition) => definition.routeSegment === normalized
  );
};

/**
 * Vertical for a URL segment, but only when it is enabled and tradable.
 * `undefined` covers both "no such vertical" and "that vertical is switched
 * off" — callers redirect to the default in either case.
 */
export const getTradableTradingTypeBySegment = (
  segment?: string
): TradingTypeDefinition | undefined => {
  const definition = getTradingTypeBySegment(segment);
  if (!definition || !isTradingTypeEnabled(definition.key)) {
    return undefined;
  }
  return definition.implemented && definition.instrumentType !== null
    ? definition
    : undefined;
};

/**
 * The vertical the app lands on: the lowest-ordered enabled, implemented,
 * instrument-backed one.
 * @throws {FeatureFlagConfigError} when no vertical is tradable — a build with
 * nothing to trade is a configuration error, not an empty state.
 */
export const getDefaultTradingType = (): TradingTypeDefinition => {
  const [first] = getTradableTradingTypes();
  if (!first) {
    throw new FeatureFlagConfigError(
      "No trading type is enabled. Enable at least one of " +
        `${TRADING_TYPE_LIST.map((d) => d.key).join(", ")} via ` +
        "REACT_APP_FEATURE_<KEY> or ChainConfig.tradingTypes."
    );
  }
  return first;
};

/** Instrument type of the default vertical. */
export const getDefaultInstrumentType = (): InstrumentTypeResponse =>
  getDefaultTradingType().instrumentType as InstrumentTypeResponse;

/**
 * URL segment for an instrument type, e.g. `PERPETUAL` -> "perpetual".
 * `undefined` for an instrument type no vertical claims, so callers building a
 * URL can fall back rather than emit `/undefined/...`.
 */
export const findRouteSegmentForInstrument = (
  instrumentType?: InstrumentTypeResponse
): string | undefined => findTradingTypeByInstrument(instrumentType)?.routeSegment;

/** As above, for a registry-sourced instrument type. */
export const getRouteSegmentForInstrument = (
  instrumentType: InstrumentTypeResponse
): string => getTradingTypeByInstrument(instrumentType).routeSegment;
